import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import './styles.scss';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'ul',
  'ol',
  'li',
  'h2',
  'h3',
  'h4',
  'div',
];

const normalizeTagName = tagName => {
  if (tagName === 'b') return 'strong';
  if (tagName === 'i') return 'em';
  return tagName;
};

export const sanitizeRichTextHtml = value => {
  if (!value || typeof window === 'undefined' || !window.DOMParser) {
    return value || '';
  }

  const parser = new window.DOMParser();
  const documentValue = parser.parseFromString(
    `<div>${value}</div>`,
    'text/html'
  );
  const output = document.createElement('div');

  const sanitizeNode = node => {
    if (node.nodeType === window.Node.TEXT_NODE) {
      return document.createTextNode(node.textContent || '');
    }

    if (node.nodeType !== window.Node.ELEMENT_NODE) {
      return document.createDocumentFragment();
    }

    const tagName = node.tagName.toLowerCase();
    if (!ALLOWED_TAGS.includes(tagName)) {
      const fragment = document.createDocumentFragment();
      Array.from(node.childNodes).forEach(child => {
        fragment.appendChild(sanitizeNode(child));
      });
      return fragment;
    }

    const sanitizedTagName = normalizeTagName(tagName);
    const element = document.createElement(sanitizedTagName);
    Array.from(node.childNodes).forEach(child => {
      element.appendChild(sanitizeNode(child));
    });
    return element;
  };

  Array.from(documentValue.body.firstChild?.childNodes || []).forEach(node => {
    output.appendChild(sanitizeNode(node));
  });

  return output.innerHTML.trim();
};

export const isRichTextEmpty = value => {
  if (!value || typeof window === 'undefined' || !window.DOMParser) {
    return !String(value || '').trim();
  }

  const parser = new window.DOMParser();
  const documentValue = parser.parseFromString(value, 'text/html');
  return !documentValue.body.textContent?.trim();
};

const RichTextEditor = ({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  minHeight,
  hideToolbar,
}) => {
  const editorRef = useRef(null);
  const lastValueRef = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    const nextValue = sanitizeRichTextHtml(value || '');
    if (!editor) {
      return;
    }
    if (nextValue === lastValueRef.current && editor.innerHTML === nextValue) {
      return;
    }
    if (document.activeElement === editor && lastValueRef.current !== null) {
      return;
    }
    editor.innerHTML = nextValue;
    lastValueRef.current = nextValue;
  }, [value]);

  const emitChange = () => {
    if (!editorRef.current) return;
    const sanitized = sanitizeRichTextHtml(editorRef.current.innerHTML);
    lastValueRef.current = sanitized;
    onChange?.(sanitized);
  };

  const runCommand =
    (command, commandValue = null) =>
    event => {
      event.preventDefault();
      if (disabled) return;
      editorRef.current?.focus();
      document.execCommand(command, false, commandValue);
      emitChange();
    };

  const handlePaste = event => {
    if (disabled) return;
    event.preventDefault();
    const html = event.clipboardData.getData('text/html');
    const text = event.clipboardData.getData('text/plain');
    document.execCommand(
      'insertHTML',
      false,
      sanitizeRichTextHtml(html || text)
    );
    emitChange();
  };

  return (
    <div
      className={`rich-text-editor ${className || ''} ${
        disabled ? 'rich-text-editor--disabled' : ''
      }`}
    >
      {!hideToolbar && (
        <div className="rich-text-editor__toolbar" aria-label="Text formatting">
          <select
            className="rich-text-editor__select"
            onChange={event =>
              runCommand('formatBlock', event.target.value)(event)
            }
            disabled={disabled}
            defaultValue="p"
            aria-label="Text style"
          >
            <option value="p">Paragraph</option>
            <option value="h2">Heading</option>
            <option value="h3">Subheading</option>
          </select>
          <button
            type="button"
            className="rich-text-editor__button"
            onMouseDown={runCommand('bold')}
            disabled={disabled}
            aria-label="Bold"
          >
            B
          </button>
          <button
            type="button"
            className="rich-text-editor__button rich-text-editor__button--italic"
            onMouseDown={runCommand('italic')}
            disabled={disabled}
            aria-label="Italic"
          >
            I
          </button>
          <button
            type="button"
            className="rich-text-editor__button"
            onMouseDown={runCommand('insertUnorderedList')}
            disabled={disabled}
            aria-label="Bulleted list"
          >
            List
          </button>
          <button
            type="button"
            className="rich-text-editor__button"
            onMouseDown={runCommand('insertOrderedList')}
            disabled={disabled}
            aria-label="Numbered list"
          >
            1.
          </button>
        </div>
      )}
      <div
        ref={editorRef}
        className="rich-text-editor__content"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
      />
    </div>
  );
};

RichTextEditor.defaultProps = {
  value: '',
  onChange: null,
  disabled: false,
  placeholder: '',
  className: '',
  minHeight: '10rem',
  hideToolbar: false,
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  minHeight: PropTypes.string,
  hideToolbar: PropTypes.bool,
};

export default RichTextEditor;
